/** Mirrors the quest JSON schema exactly. Key order matters — see serializeQuest. */

export const QUALITIES = ['Basic', 'Fine', 'Superior', 'Flawless'] as const
export type Quality = (typeof QUALITIES)[number]

/** Price multiplier applied to saleBase, then truncated. */
export const QUALITY_MULTIPLIER: Record<Quality, number> = {
  Basic: 1.0,
  Fine: 1.1,
  Superior: 1.2,
  Flawless: 1.5,
}

export const TAGS = ['TUTORIAL', 'MAIN', 'LOCATION', 'TOOL', 'SIDE'] as const
export type Tag = (typeof TAGS)[number]

/** Prereq types taking a bare numeric `value`. */
export const LEVEL_PREREQ_TYPES = [
  'RenownLevel',
  'TotalLevel',
  'Mining',
  'Woodcutting',
  'Hunting',
  'Smithing',
  'Jeweler',
] as const
export type LevelPrereqType = (typeof LEVEL_PREREQ_TYPES)[number]

export const PREREQ_TYPES = [
  'QuestCompleted',
  'LocationVisit',
  ...LEVEL_PREREQ_TYPES,
] as const
export type PrereqType = (typeof PREREQ_TYPES)[number]

export type Prereq =
  | { type: 'QuestCompleted'; quest: string }
  | { type: 'LocationVisit'; location: string; value: number }
  | { type: LevelPrereqType; value: number }

export const SPECIAL_REWARD_TYPES = ['Tool', 'Recipe', 'Skin', 'Boost'] as const
export type SpecialRewardType = (typeof SPECIAL_REWARD_TYPES)[number]

export type SpecialReward =
  | { type: 'Tool'; tool: string }
  | { type: 'Recipe'; recipe: string }
  | { type: 'Skin'; skin: string }
  | { type: 'Boost'; boost: string; amount: number }

export interface ItemRef {
  item: string
  amount: number
  quality: Quality
}

export interface BoostRequirement {
  boost: string
  amount: number
}

/** Unity's output. Regenerated on export, discarded on import. */
export interface Computed {
  estimatedValue: number
  actualValue: number
  gatingLevel: number
  requirementLevels: string
}

export interface Quest {
  id: string
  label: string
  tag: Tag
  /** Work in progress: kept in the project but never offered to the player. */
  disabled: boolean
  description: string
  completionText: string
  sprite: string | null
  prereqs: Prereq[]
  requirements: ItemRef[]
  requirementsToShow: ItemRef[]
  goldRequirement: number
  goldRequirementToShow: number
  renownRequirement: number
  estateRequirement: string[]
  boostRequirement: BoostRequirement[]
  challengeStarsObtained: number
  rewards: ItemRef[]
  specialRewards: SpecialReward[]
  goldReward: number
  computed: Computed
}

/**
 * The exact key order Unity's exporter emits. All 59 shipped files share it.
 * Writing keys in any other order produces a spurious whole-file diff.
 */
export const QUEST_KEY_ORDER: (keyof Quest)[] = [
  'id',
  'label',
  'tag',
  'disabled',
  'description',
  'completionText',
  'sprite',
  'prereqs',
  'requirements',
  'requirementsToShow',
  'goldRequirement',
  'goldRequirementToShow',
  'renownRequirement',
  'estateRequirement',
  'boostRequirement',
  'challengeStarsObtained',
  'rewards',
  'specialRewards',
  'goldReward',
  'computed',
]

export const COMPUTED_KEY_ORDER: (keyof Computed)[] = [
  'estimatedValue',
  'actualValue',
  'gatingLevel',
  'requirementLevels',
]

export function emptyQuest(id: string): Quest {
  return {
    id,
    label: '',
    tag: 'SIDE',
    disabled: false,
    description: '',
    completionText: '',
    sprite: null,
    prereqs: [],
    requirements: [],
    requirementsToShow: [],
    goldRequirement: 0,
    goldRequirementToShow: 0,
    renownRequirement: 0,
    estateRequirement: [],
    boostRequirement: [],
    challengeStarsObtained: 0,
    rewards: [],
    specialRewards: [],
    goldReward: 0,
    computed: {
      estimatedValue: 0,
      actualValue: 0,
      gatingLevel: 0,
      requirementLevels: 'No requirement',
    },
  }
}
